/**
 * Compiled-screen contract — the SINGLE definitions shared by the compile-time
 * writer (`compiledScreenMaterializers.ts`) and the health checks that can
 * refuse that compile (`strategyHealthReport.ts`).
 *
 * Why this module exists (W2 fix round 1, reviewer finding 1): the two files
 * had byte-identical copies of the literal-currency pattern, of the pick-screen
 * type set, and of the paywall-variable list in two different shapes. The
 * writer decides what to materialize and the checker decides what refuses a
 * compile, so a one-sided edit produces a card the compiler considers clean and
 * the health check rejects — an unfixable compile. Both now import from here.
 */

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ---------------------------------------------------------------------------
// Paywall price contract (D-T9b-1, ruling R-W0-9 / R-W2-1)
// ---------------------------------------------------------------------------

/**
 * The card slot compile owns. It is the Paywall template struct's own price
 * default (`V2TemplateStructMerger`) — the slot that shipped `17,99 US$/month`
 * on every compiled flagship paywall — and the model never writes it
 * (`SimplifiedV2Mapper` maps the generated subtitle to `bottomStartLabel`).
 */
export const PAYWALL_PRICE_SLOT = 'topEndLabel';

/**
 * The text compile writes into that slot. Resolved PER PRODUCT at runtime by
 * `useV2Purchase.itemVariablesMap`, picked up in `ProductCatalog` as
 * `itemVariablesMap[item.productId] || variables`, so each card shows its own
 * price and the copy survives a product change (ruling R-W0-9).
 *
 * `@price` alone, NOT `@price / @interval`:
 * `priceFormatter.getUniversalLocalizedPrice` already returns the price WITH
 * its interval (`displayPrice = "$47.90/year"`) while `@interval` is the
 * separate count form ("1 year"). The first W2 walk rendered
 * `$47.90/year / 1 year` on both cards — proof read off the published funnel.
 *
 * What every paywall variable renders is tabulated ONCE, in
 * `modules/template-tags/MODULE.md` § Paywall Variables (W8 a′); the prompt
 * policy (`block-screen-agent-policy.json`, "Paywall copy boundary") and this
 * constant both cite that table instead of restating it.
 */
export const PAYWALL_PRICE_TEXT = '@price';

/** The four label slots of a product card, all rendered through `<Text variables>`. */
export const PRODUCT_LABEL_SLOTS = [
  'topStartLabel',
  'topEndLabel',
  'bottomStartLabel',
  'bottomEndLabel',
] as const;

/**
 * Runtime paywall variables (`modules/template-tags` § Paywall Variables),
 * resolved from `paymentConfig.preBakedPrices`. Longest-first so a prefix
 * (`price`) can never consume a longer name (`priceAndCurrency`) while
 * stripping tokens out of a string.
 */
export const PAYWALL_PRICE_VARIABLES: readonly string[] = [
  'priceAndCurrency',
  'priceandcurrency',
  'fullTrialDays',
  'introDuration',
  'introProduct',
  'introPrice',
  'trialDays',
  'duration',
  'interval',
  'price',
];

/** Any paywall variable in a string means its numbers are catalog-driven. */
export const PAYWALL_VARIABLE_TOKEN_PATTERN = new RegExp(
  `@(?:${PAYWALL_PRICE_VARIABLES.join('|')})\\b`,
  'i',
);

/**
 * A currency symbol or ISO code adjacent to a number. Deliberately narrow:
 * `100%`, `16:8` and `3 meals` must not match; `17,99 US$`, `$47.90`,
 * `EUR 9,99` and `9.99 USD` must.
 */
export const LITERAL_CURRENCY_AMOUNT_PATTERN =
  /(?:[$€£¥₽₹]\s?\d|\b(?:US\$|USD|EUR|GBP|RUB|INR|CHF|CAD|AUD)\s?\d|\d[\d.,]*\s?(?:US\$|USD|EUR|GBP|RUB|INR|CHF|CAD|AUD|[$€£¥₽₹]))/i;

// ---------------------------------------------------------------------------
// Screen classes
// ---------------------------------------------------------------------------

/** Screen types whose answer is a picked option (auto-advance on tap). */
export const PICK_SCREEN_TYPES: ReadonlySet<string> = new Set([
  'ListSinglePick',
  'ListMultiPick',
  'GridSinglePick',
  'GridMultiPick',
]);

/**
 * Pick screens where tapping an option NAVIGATES, so a per-option
 * `action.edges` is a real routing mechanism (D-W6-3).
 *
 * On a MULTI pick a tap toggles a selection and the CTA navigates, so per
 * option edges there route nothing — a multi-pick screen can never be the
 * evidence that a routing contract is fulfilled.
 */
export const SINGLE_PICK_SCREEN_TYPES: ReadonlySet<string> = new Set([
  'ListSinglePick',
  'GridSinglePick',
]);

/** V2 screen types whose value is typed (and therefore can be left empty). */
export const INPUT_SCREEN_TYPES: ReadonlySet<string> = new Set(['TextInput']);

// ---------------------------------------------------------------------------
// Screen ↔ variable resolution
// ---------------------------------------------------------------------------

export interface CollectedVariable {
  /** The key the variable is stored under (may differ from its declared `id`). */
  variableKey: string;
  variableId: string;
  definition: Record<string, unknown>;
}

export interface CollectedVariableIndex {
  /** Screen id → the variable that screen collects. */
  byScreenId: Map<string, CollectedVariable>;
  /** Variable key AND declared id → every screen that collects it, sorted. */
  screenIdsByVariable: Map<string, string[]>;
}

interface IndexableScreen {
  screenType?: string;
  content?: unknown;
  aiMeta?: unknown;
}

/**
 * Both directions of the screen ↔ variable binding, resolved ONCE for the
 * writer and the checkers.
 *
 * `boundScreenIds` / `screenBindings` are the DECLARATION;
 * `aiMeta.variableRealizations` is the generator's own record and survives a
 * binding that was never remapped — the same order `stampRequiredInputGates`
 * established for D-T9a-8. The parity check used to read the declaration only,
 * so a pick screen bound solely through a realization was silently skipped
 * (W2 fix round 1, reviewer finding 3).
 */
export function buildCollectedVariableIndex(
  screens: Record<string, IndexableScreen>,
  variables: Record<string, unknown>,
): CollectedVariableIndex {
  const byScreenId = new Map<string, CollectedVariable>();
  const screenIdsByVariable = new Map<string, Set<string>>();
  const byKey = new Map<string, CollectedVariable>();

  const remember = (variableKeys: string[], screenId: string): void => {
    for (const key of variableKeys) {
      if (!key) continue;
      const bucket = screenIdsByVariable.get(key) ?? new Set<string>();
      bucket.add(screenId);
      screenIdsByVariable.set(key, bucket);
    }
  };

  for (const [variableKey, definition] of Object.entries(variables)) {
    if (!isRecord(definition)) continue;
    const variableId = typeof definition.id === 'string' && definition.id ? definition.id : variableKey;
    const entry: CollectedVariable = { variableKey, variableId, definition };
    byKey.set(variableKey, entry);
    byKey.set(variableId, entry);

    const bound = Array.isArray(definition.boundScreenIds)
      ? definition.boundScreenIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
      : Object.keys(isRecord(definition.screenBindings) ? definition.screenBindings : {});
    for (const screenId of bound) {
      if (!byScreenId.has(screenId)) byScreenId.set(screenId, entry);
      remember([variableKey, variableId], screenId);
    }
  }

  for (const [screenId, screen] of Object.entries(screens)) {
    // A null/undefined/array entry is a corrupt document, not a crash: reading
    // `screen.aiMeta` off one threw INSIDE the compile and took the whole
    // transform down with an unroutable TypeError (fold-in, final review of the
    // W1–W6 run). Skipping leaves the defect to the health checks, which can
    // name it.
    if (!isRecord(screen)) continue;
    const aiMeta = isRecord(screen.aiMeta) ? screen.aiMeta : null;
    const realizations = aiMeta && Array.isArray(aiMeta.variableRealizations) ? aiMeta.variableRealizations : [];
    for (const realization of realizations) {
      if (!isRecord(realization) || realization.realizationKind !== 'collect') continue;
      const variableId = typeof realization.variableId === 'string' ? realization.variableId : '';
      const entry = variableId ? byKey.get(variableId) : undefined;
      if (!entry) continue;
      if (!byScreenId.has(screenId)) byScreenId.set(screenId, entry);
      remember([entry.variableKey, entry.variableId], screenId);
      break;
    }
  }

  return {
    byScreenId,
    screenIdsByVariable: new Map(
      [...screenIdsByVariable].map(([key, set]) => [key, [...set].sort()]),
    ),
  };
}

// ---------------------------------------------------------------------------
// Back affordance (W11 item 4 — the WARNING's screen classes, owner ruling
// R-POST-3; D-T9b-17 / S-10 as the reason the gap is worth naming)
// ---------------------------------------------------------------------------

/**
 * Screen types on which a MISSING retreat affordance is worth reporting.
 *
 * Pick screens auto-advance on tap (D-T9b-17): without a retreat trigger a
 * mis-tap is uncorrectable. TextInput screens carry the same argument (S-10) —
 * the template already renders `header.back` whenever the compiled screen
 * carries one (`screen-view-v2/components/screens/TextInput`). The launch
 * screen is excluded: there is nothing behind it and `navigateBack` would fail
 * with no history.
 *
 * **The compiler no longer SEEDS the affordance (owner ruling R-POST-3, W11
 * item 4).** `materializeBackAffordance` used to add a `header.back` retreat
 * trigger labelled "Back" — the platform putting an English word in front of a
 * visitor, the same class R-W70-38 forbade for the disclosure labels, and an
 * affordance the owner never asked for. Compile now transports an AUTHORED
 * `header.back` unchanged and `graph.noRetreatAffordance`
 * (`strategyHealthReport.ts`) WARNS where there is none. The opt-in carrier —
 * how an owner asks for one — is the owner's to define; the compiler invents
 * neither the affordance nor its copy.
 *
 * This set is therefore the CHECKER's, not a writer's; it stays here because
 * the screen classes it composes are the contract's.
 */
export const BACK_AFFORDANCE_SCREEN_TYPES: ReadonlySet<string> = new Set([
  ...PICK_SCREEN_TYPES,
  ...INPUT_SCREEN_TYPES,
]);

// ---------------------------------------------------------------------------
// Option item layouts (W7(f) compile half, ruling R-W70-16)
// ---------------------------------------------------------------------------

/**
 * `itemLayout` values whose `OptionsItem` variant renders `item.subtitle`
 * (`screen-view-v2/components/shared/OptionsItem/OptionsItem.tsx` VARIANT_MAP).
 * Any other layout DROPS the subtitle at render, so a compiled subtitle under
 * one is copy the reader never sees.
 */
export const SUBTITLE_RENDERING_ITEM_LAYOUTS: ReadonlySet<string> = new Set([
  'ImageTitleSubtitle',
  'TitleSubtitleImage',
  'TitleSubtitle',
  'TitleSubtitleCheckbox',
  'ImageTitleSubtitleCheckbox',
  'CheckboxTitleSubtitle',
  'CheckboxTitleSubtitleImage',
]);

/**
 * The promotions compile is allowed to apply automatically (R-W70-16).
 *
 * ONLY `ImageTitle → ImageTitleSubtitle`: that pair is proved at 320 by an
 * existing rendering — `ImageTitleSubtitle` already ships the identical
 * geometry (image + a content column with title above subtitle) — so the
 * promotion cannot clip. Every other subtitle-less layout that carries
 * subtitles gets a WARNING instead of a silent geometry change; W7 does not
 * guess a target whose 320 rendering nobody has measured.
 */
export const ITEM_LAYOUT_SUBTITLE_PROMOTIONS: ReadonlyMap<string, string> = new Map([
  ['ImageTitle', 'ImageTitleSubtitle'],
]);

// ---------------------------------------------------------------------------
// Numeric plausibility bounds (W8(d) / D-W2-3, ruling R-W8A-5)
// ---------------------------------------------------------------------------

/**
 * One row of the plausibility table: a recognisable KIND of quantity and the
 * range a self-reported answer to it can honestly take.
 *
 * `subject` is matched against the variable's id + name — WHAT is being asked.
 * `unit` is matched against the variable's own `UNIT:` clause + name — the
 * units it is asked in. Both must match, so "Time spent cooking (minutes)"
 * never picks up the height row and a `weight` variable measured in pounds
 * never picks up the kilogram row (no pound/inch rows exist yet — an
 * unrecognised quantity gets NO bound rather than a guessed one).
 */
export interface NumericPlausibilityBound {
  /** Stable id, quoted in the compile warning so the choice is auditable. */
  kind: string;
  subject: RegExp;
  unit: RegExp;
  /**
   * Disqualifies a subject that matched anyway. A row this narrow needs one
   * whenever its subject words can appear inside a DIFFERENT quantity's name
   * (fix round 1 of the W8a review): "Current weight at the start of your
   * weight-loss plan (kg)" matches `weight-change-kg`'s `loss` while being a
   * body weight, and would have capped a 70 kg visitor at 50.
   */
  subjectExcludes?: RegExp;
  min?: number;
  max?: number;
  /** Why these numbers — the record a reviewer reads instead of guessing. */
  rationale: string;
}

/**
 * The plausibility table, first match wins.
 *
 * **It fills, it never overrides.** A bound the variable DECLARES always wins
 * (`materializeInputConstraints`), for the same reason W2 refused to fake a
 * range into a regex: the compiler does not overrule an author. That is why
 * the protein row's minimum is here and still does not fire on the flagship,
 * whose protein share declares `min: 0` — teaching the GENERATOR to declare 10
 * is the prompt half (hand-over to W8b).
 *
 * Ordering is load-bearing: `macro-share-protein` before `macro-share`, and
 * `weight-change-kg` before `body-weight-kg`, because "Chosen weight gain
 * (kg)" matches both and the narrower row is the right one.
 *
 * The numbers are deliberately generous. Refusing a real visitor's true answer
 * is a worse failure than accepting an implausible one, so each maximum sits
 * beyond the most extreme documented human value rather than at a "typical"
 * ceiling.
 */
export const NUMERIC_PLAUSIBILITY_BOUNDS: readonly NumericPlausibilityBound[] = [
  {
    kind: 'macro-share-protein',
    subject: /protein/,
    unit: /percent|%/,
    min: 10,
    max: 100,
    rationale:
      'A macronutrient split is a share of 100. Under 10 % protein is not a split anyone chose '
      + 'deliberately — it is a mistyped field — and over 100 is not a share at all.',
  },
  {
    kind: 'macro-share',
    subject: /carbohydrate|carb|\bfat\b|macro|share|split/,
    unit: /percent|%/,
    min: 0,
    max: 100,
    rationale: 'A share of a macronutrient split cannot exceed the whole it is a share of.',
  },
  {
    kind: 'age-years',
    subject: /\bage\b|\byears? old\b/,
    unit: /year/,
    min: 13,
    max: 120,
    rationale:
      'Under 13 a visitor cannot lawfully consent to the processing this funnel does '
      + '(COPPA / GDPR Art. 8 floor), and 120 sits above the oldest verified human lifespan (122).',
  },
  {
    kind: 'height-cm',
    subject: /height|\btall\b/,
    unit: /\bcm\b|centimet/,
    min: 50,
    max: 260,
    rationale:
      'Below the shortest documented adult (63 cm) and above the tallest living man (251 cm), '
      + 'so no real answer is refused.',
  },
  {
    kind: 'weight-change-kg',
    // Word-bounded, and a DELTA word only. `goal`/`target` are deliberately NOT
    // here: "Chosen goal weight (kg)" is a target BODY weight (65 kg), not a
    // difference, and belongs to the row below.
    subject: /\b(?:gain|gains|loss|lose|losing|shed|shedding|dropped)\b|\bweight\s+change\b/,
    // A NOUN PHRASE naming the visitor's present body weight wins over a delta
    // verb in the same sentence — "Current weight at the start of your
    // weight-loss plan (kg)" is a body weight, not a goal.
    //
    // Narrowed in fix round 2: the first version excluded a bare `start`/`now`
    // anywhere in the name, which sent a genuine goal — "How much weight do you
    // want to lose starting now? (kg)" — into the body-weight band and refused
    // any answer under 25. Only `<state> weight` / `weight now` counts, and the
    // separator accepts `_` so the id form (`setup_current_weight_kg`) matches
    // as well as the name.
    subjectExcludes:
      /(?:^|[^a-z])(?:current|body|starting|present|existing|baseline|todays)[\s_-]+weight\b|\bweight[\s_-]+(?:now|today)\b/,
    unit: /\bkg\b|kilogram/,
    min: 0.5,
    max: 50,
    rationale:
      'A weight-change GOAL, not a body weight: under half a kilo is not a goal anyone sets, '
      + 'and 50 kg is beyond the largest goal a self-reported onboarding answer carries.',
  },
  {
    kind: 'body-weight-kg',
    subject: /\bweight\b|\bmass\b/,
    unit: /\bkg\b|kilogram/,
    min: 25,
    max: 400,
    rationale:
      'A body weight in kilograms. 25 is below the lightest documented adult and 400 above the '
      + 'heaviest an onboarding answer plausibly carries, so the bound catches a typo (`700`, a '
      + 'value typed in pounds) without refusing a real person.',
  },
];

/** The `UNIT: …` clause the collection contract writes into a variable's description. */
const UNIT_CLAUSE_PATTERN = /UNIT:\s*([^.\n]*)/i;

/**
 * Resolve the plausibility row for a collected numeric variable, or `null`
 * when nothing in the table recognises it.
 *
 * Detection runs off the variable's OWN statement of what it collects — the
 * same discipline as the S-1 time field, which is recognised from the field's
 * placeholder and error copy rather than from a name test. The long prose of
 * `description` is deliberately NOT scanned whole: a weight-loss task's
 * description mentions kilograms in every sentence, so only its `UNIT:` clause
 * counts as a statement of units.
 */
export function resolveNumericPlausibilityBound(
  variableId: string,
  definition: Record<string, unknown>,
): NumericPlausibilityBound | null {
  const name = typeof definition.name === 'string' ? definition.name : '';
  const declaredId = typeof definition.id === 'string' && definition.id ? definition.id : variableId;
  const description = typeof definition.description === 'string' ? definition.description : '';
  const unitClause = UNIT_CLAUSE_PATTERN.exec(description)?.[1] ?? '';

  const subjectText = `${declaredId} ${variableId} ${name}`.toLowerCase();
  const unitText = `${unitClause} ${name}`.toLowerCase();

  return NUMERIC_PLAUSIBILITY_BOUNDS.find(
    (bound) => bound.subject.test(subjectText)
      && !(bound.subjectExcludes?.test(subjectText) ?? false)
      && bound.unit.test(unitText),
  ) ?? null;
}

// ---------------------------------------------------------------------------
// Build-time vocabulary in consumer copy (W8 M2, ruling R-W8A-6)
// ---------------------------------------------------------------------------

/**
 * Phrases that say "this is not the real product" to someone the funnel is
 * about to charge. The W6-fix flagship shipped `screen51.copy.subtitle` =
 * "Test checkout offer: …" against a live $47.90 Stripe sheet.
 *
 * **Phrasal, never bare tokens.** A check that can refuse a compile must not
 * refuse real copy: "test" alone appears in "test your limits" and "your latest
 * blood test", "sample" in "sample meal plan", "trial" in every honest paywall.
 *
 * Fix round 1 of the W8a review removed the last four bare tokens, which all had
 * innocent readings a real funnel can write: "A sandboxed workout plan",
 * "cancer staging", "dummy use" in a parenting funnel, "Placeholder-free
 * planning". Every entry is now a multi-word phrase or a token with no innocent
 * reading at all (`lorem ipsum`, `example.com`, `TODO`).
 *
 * The list is the ONE home for the checker (`strategyHealthReport`) and for
 * anything else that has to name the same vocabulary; the prompt-policy half
 * (W8b) cites it rather than restating it.
 */
export const TEST_VOCABULARY_PHRASES: readonly { phrase: string; pattern: RegExp }[] = [
  { phrase: 'test checkout', pattern: /\btest\s+checkout\b/i },
  { phrase: 'test mode', pattern: /\btest\s+mode\b/i },
  { phrase: 'test offer', pattern: /\btest\s+offer\b/i },
  { phrase: 'test purchase', pattern: /\btest\s+(?:purchase|payment|order|subscription|product|plan|price|card)\b/i },
  { phrase: 'test environment', pattern: /\btest\s+(?:environment|data|account|user|only)\b/i },
  { phrase: 'this is a test', pattern: /\b(?:this\s+is|it['’]s)\s+(?:just\s+)?a\s+test\b/i },
  { phrase: 'for testing', pattern: /\b(?:for|during)\s+testing\b|\btesting\s+(?:only|purposes)\b/i },
  { phrase: 'sandbox mode', pattern: /\bsandbox(?:ed)?\s+(?:mode|environment|checkout|account|payment|key|keys)\b/i },
  { phrase: 'staging environment', pattern: /\bstaging\s+(?:environment|server|site|build|url|api)\b/i },
  { phrase: 'demo mode', pattern: /\bdemo\s+(?:mode|only|offer|checkout|purchase|product|account|data|version|copy)\b/i },
  { phrase: 'dummy data', pattern: /\bdummy\s+(?:data|text|content|price|prices|value|values|product|copy)\b/i },
  { phrase: 'placeholder text', pattern: /\bplaceholder\s+(?:text|copy|content|image|value|title|subtitle)\b/i },
  { phrase: 'lorem ipsum', pattern: /\blorem\s+ipsum\b/i },
  { phrase: 'mock data', pattern: /\bmock\s+(?:data|price|prices|product|checkout|payment)\b/i },
  { phrase: 'example.com', pattern: /\bexample\.(?:com|org|net)\b/i },
  { phrase: 'TODO', pattern: /\b(?:TODO|FIXME|TBD|XXX)\b/ },
  { phrase: 'not a real charge', pattern: /\bnot\s+a\s+real\s+(?:charge|payment|purchase|product)\b/i },
];

/** The first phrase this text uses, or `null`. */
export function findTestVocabularyPhrase(text: string): string | null {
  return TEST_VOCABULARY_PHRASES.find((entry) => entry.pattern.test(text))?.phrase ?? null;
}

// ---------------------------------------------------------------------------
// Option list order and length (W8 S3, rulings R-W8A-7 / R-W8A-8)
// ---------------------------------------------------------------------------

/**
 * The number of options a single-pick screen shows before the rest go behind
 * the runtime's "More options" disclosure
 * (`screen-view-v2/components/shared/OptionsList`, which carries the same
 * number as `OPTIONS_DISCLOSURE_THRESHOLD` — it cannot import this module).
 *
 * survey-methodology-and-measurement.md § 5.3: past ~8 visible options a
 * single-pick measures reading stamina rather than preference.
 */
export const SINGLE_PICK_OPTION_CAP = 8;

/**
 * An option value or label that carries an ORDER — a frequency, a range, a
 * ladder, a stage. Such a list must NEVER be randomized (§ 4.2): shuffling
 * "never / sometimes / always" destroys the scale it measures.
 *
 * Shared with `screenValidator.ts`, which applies the same test at generation
 * time (`autoflagSurveyRandomization`, D5). Two copies of this pattern would
 * let a screen the generator refused to flag be flagged by the compiler, or
 * the reverse — the exact split `compiledScreenContract` exists to prevent.
 */
export const ORDERED_OPTION_SCALE_PATTERN =
  /^\d|\d+\s*[-–]\s*\d+|never|rarely|sometimes|often|always|daily|weekly|monthly|yearly|beginner|intermediate|advanced|less_|more_|under_|over_|_low|_high|_medium/i;

/**
 * An option that must stay in its authored position — an opt-out, an "other",
 * a "none of the above". The runtime shuffle
 * (`screen-view-v2/utils/shuffle.seededFisherYatesShuffle`) has no concept of
 * an anchored item, so a list containing one is left unrandomized rather than
 * scattered (ruling R-W8A-7). It is also the set the runtime keeps VISIBLE
 * while a long list is collapsed (§ 6.2), through a mirror of this pattern.
 *
 * The `(?![a-z])` followers are a trailing boundary a plain `\b` cannot give:
 * `_` is a word character, so `\b` would refuse `prefer_not_to_say` while
 * still accepting "I prefer nothing sweet" (fix round 2).
 */
export const ANCHORED_OPTION_VALUE_PATTERN =
  /prefer[\s_-]?not(?![a-z])|no[\s_-]?answer(?![a-z])|rather[\s_-]?not(?![a-z])|not[\s_-]?say(?![a-z])|not[\s_-]?sure(?![a-z])|none[\s_-]?of(?![a-z])|^none$|^other$|_other$|^skip$/i;

// ---------------------------------------------------------------------------
// Option-label cross-binding thresholds (W8 M5, fix round 1)
// ---------------------------------------------------------------------------

/**
 * A compiled option label is CROSS-BOUND when another canonical option matches
 * it clearly better than the option it is attached to
 * (`strategyHealthReport.findOptionBindingParityDefects`).
 *
 * These two numbers live here, with the rest of the compile contract, because
 * since W8(c) they gate a compile REFUSAL: `options.optionBindingParity` is
 * `blocking` under the D62 profile, and a threshold that can refuse a compile
 * belongs in the one place the writer and the checker share, not buried in the
 * checker.
 *
 * Both are deliberately conservative: the reworded-but-faithful labels that are
 * the D-T9a-9 norm (33 paraphrases, not permutations) must never fire. On the
 * W6-fix candidate all 14 findings scored 0.778-1.0 against an own-similarity
 * of 0-0.182 — no near-threshold call.
 */
export const CROSSBOUND_MIN_SIMILARITY = 0.4;
export const CROSSBOUND_MIN_MARGIN = 0.2;
